import React, { FormEvent, MouseEvent, useState } from 'react'
import styled from '@emotion/styled'
import { callCenterPhoneNumber } from '@mobi/utils'
import { FeatureFlags, hexColors } from '@mobi/settings'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { ButtonBlock } from '@core/Components/Buttons/ButtonBlock'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { InputField } from '@mobi/component-library/Legacy/Input/InputField'
import { SpinnerInlineStyled } from '@mobi/component-library/Legacy/Spinner/Spinner.styles'
import { NoticeBoxSingle } from '@core/Components/NoticeBox'
import {
  trackLoginForgotAccountNumber,
  trackLoginForgotPassword,
} from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { LoginFieldOptionsStyled } from '../../Login.styles'
import type { useLoginState } from '../../hooks/useLoginState'
import { trackSignUpClick } from '@mobi/account/Areas/SignUp/Utils/ga'

import { LoginMainBiometricsToggle } from './LoginMainBiometricsToggle'
import { useFeature } from '@core/Utils/hooks'

export const LoginMain = ({ state, handlers }: ReturnType<typeof useLoginState>) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const [isBiometricsStatusReady, setIsBiometricsStatusReady] = useState(false)
  const [isBiometricsToggleOn, setIsBiometricsToggleOn] = useState(false)
  const [isBiometricsToggleDifferent, setIBiometricsToggleDifferent] = useState(false)
  const [hasAccountIdentifierChanged, setHasAccountIdentifierChanged] = useState(false)

  const isValid = !!state.accountIdentifier && !!state.password
  const shouldShowKeepMeLoggedIn = isReactNativeApp
  const shouldDisableSubmit =
    state.isBusy ||
    !!state.errorMessage ||
    !isValid ||
    (isReactNativeApp && !isBiometricsStatusReady)

  const isEmailLoginFeatureActive = useFeature(FeatureFlags.EMAIL_LOGIN.key)

  const handleRecoverAccountNumber = () => {
    handlers.setWorkflow('Account Number Recovery')
    trackLoginForgotAccountNumber()
  }
  const handleRecoverPassword = () => {
    handlers.setWorkflow('Password Reset')
    trackLoginForgotPassword(state.accountIdentifier)
  }

  const doLogin = () => {
    handlers.login({
      userHasToggledBiometrics: isBiometricsToggleDifferent,
      isBiometricsToggleOn,
      hasAccountIdentifierChanged,
      withMfa: false,
    })
  }

  const handleSubmit = (e: MouseEvent<HTMLButtonElement> | FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (shouldDisableSubmit) return
    doLogin()
  }

  const handleEnterKey: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (!shouldDisableSubmit && (e.key === 'Enter' || e.keyCode === 13)) {
      doLogin()
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={state.isBusy || !!state.mfaFactor}>
        {isEmailLoginFeatureActive ? (
          <InputField
            name='Account Number or Verified Email'
            autoComplete='username'
            value={state.accountIdentifier}
            onChange={e => {
              handlers.onAccountIdentifierChange(e)
              setHasAccountIdentifierChanged(true)
            }}
            onKeyUp={handleEnterKey}
            maxLength={254}
            type='text'
            data-tid-login-account
          />
        ) : (
          <InputField
            name='Account Number'
            autoComplete='username'
            value={state.accountIdentifier}
            onChange={handlers.onAccountIdentifierChange}
            onKeyUp={handleEnterKey}
            maxLength={8}
            type='tel'
            data-tid-login-account
          />
        )}

        <LoginFieldOptionsStyled>
          <label htmlFor='remember-input'>
            <input
              id='remember-input'
              type='checkbox'
              checked={state.rememberAccount}
              onChange={handlers.toggleRememberAccount}
            />
            <span>Remember Account</span>
          </label>

          <button
            type='button'
            onClick={handleRecoverAccountNumber}
            data-testid='login-forgot-account-number'
          >
            Forgot account number
          </button>
        </LoginFieldOptionsStyled>
      </fieldset>

      <fieldset disabled={state.isBusy || !!state.mfaFactor}>
        <InputField
          name='Password'
          autoComplete='current-password'
          type='password'
          value={state.password}
          onChange={handlers.onPasswordChange}
          onKeyUp={handleEnterKey}
          addOnIcon={isPasswordVisible ? 'CustomVisibilityOff' : 'CustomVisibilityOn'}
          addonAriaLabel={isPasswordVisible ? 'Hide password' : 'Show password'}
          handleAddOnAction={() => setIsPasswordVisible(isVisible => !isVisible)}
          data-tid-login-password
        />

        <LoginFieldOptionsStyled>
          {shouldShowKeepMeLoggedIn ? (
            <label htmlFor='keep-logged-in'>
              <input
                id='keep-logged-in'
                type='checkbox'
                checked={state.keepLoggedIn}
                onChange={handlers.toggleKeepLoggedIn}
                data-testid='login-keep-logged-in'
              />
              <span>Keep me logged in</span>
            </label>
          ) : (
            <div />
          )}

          <button type='button' onClick={handleRecoverPassword} data-testid='login-forgot-password'>
            Forgot password
          </button>
        </LoginFieldOptionsStyled>
      </fieldset>

      {isReactNativeApp ? (
        <LoginMainBiometricsToggle
          onStatusChanged={isReady => setIsBiometricsStatusReady(isReady)}
          onToggle={(newValue, isDifferent) => {
            setIsBiometricsToggleOn(newValue)
            setIBiometricsToggleDifferent(isDifferent)
          }}
        />
      ) : null}

      {state.errorMessage ? (
        <NoticeBoxSingle title={state.errorMessage} noticeBoxType='Error' hasBorder />
      ) : null}

      <LoginMainFooterStyled>
        <ButtonBlock
          type='submit'
          color='primary'
          id='submit-login'
          data-testid='login-submit'
          data-tid-login-submit
          onClick={handleSubmit}
          disabled={shouldDisableSubmit}
        >
          {state.isBusy ? <SpinnerInlineStyled size={2} /> : 'Log In'}
        </ButtonBlock>

        <div role='separator'>OR</div>

        <ButtonBlock
          type='button'
          color='secondary'
          onClick={() => {
            trackSignUpClick('Login Modal')
            handlers.signUp()
          }}
          data-tid-submit-signup
        >
          Join Now
        </ButtonBlock>

        <a href={callCenterPhoneNumber}>
          <Icon type='phone' size='2rem' /> For assistance, please call <u>1300 36 36 88</u>
        </a>
      </LoginMainFooterStyled>
    </form>
  )
}

// ======
// Styles
// ======

const LoginMainFooterStyled = styled('div')({
  marginBottom: '1.5rem',

  'div[role="separator"]': {
    display: 'flex',
    alignItems: 'center',
    margin: '2rem auto',
    maxWidth: '70%',
    fontSize: '1.3rem',
    color: hexColors.white,

    '&::before, &::after': {
      content: '""',
      flex: 1,
      height: 1,
      margin: '0 0.5rem',
      background: hexColors.white,
      opacity: 0.5,
    },
  },

  'a[href^="tel:"]': {
    display: 'block',
    margin: '2.5rem 0',
    fontSize: '1.2rem',
    textAlign: 'center',
    textDecoration: 'none',
    color: hexColors.white,

    svg: {
      marginRight: '0.5rem',
    },
  },
})
